import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Textarea } from '../components/ui/textarea';
import { 
    Mail,
    Calendar,
    Building2,
    User,
    Music,
    DollarSign,
    Clock,
    CheckCircle,
    XCircle,
    Loader2,
    ExternalLink,
    MessageSquare,
    ArrowRight,
    Lock
} from 'lucide-react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

export default function InvitesPage() {
    const { token } = useAuth();
    const navigate = useNavigate();
    const [invites, setInvites] = useState([]);
    const [loading, setLoading] = useState(true);
    const [actionDialog, setActionDialog] = useState({ open: false, invite: null, action: null });
    const [declineReason, setDeclineReason] = useState('');
    const [processing, setProcessing] = useState(false);

    const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

    useEffect(() => {
        fetchInvites();
    }, []);

    const fetchInvites = async () => {
        try {
            const response = await fetch(`${API}/invites`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setInvites(data);
            }
        } catch (error) {
            toast.error('Error loading invites');
        } finally {
            setLoading(false);
        }
    };

    const handleAccept = async () => {
        if (!actionDialog.invite) return;
        
        setProcessing(true);
        try {
            const response = await fetch(`${API}/invites/${actionDialog.invite.id}/accept`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (response.ok) {
                const data = await response.json();
                toast.success('Invite accepted! Redirecting to project...');
                setActionDialog({ open: false, invite: null, action: null });
                // Redirect to submission page
                setTimeout(() => {
                    navigate(`/posted-projects/${actionDialog.invite.project_id}`);
                }, 1000);
            } else {
                const error = await response.json();
                toast.error(error.detail || 'Failed to accept invite');
            }
        } catch (error) {
            toast.error('Error accepting invite');
        } finally {
            setProcessing(false);
        }
    };

    const handleDecline = async () => {
        if (!actionDialog.invite) return;
        
        setProcessing(true);
        try {
            const response = await fetch(`${API}/invites/${actionDialog.invite.id}/decline`, {
                method: 'POST',
                headers: { 
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ reason: declineReason || null })
            });
            
            if (response.ok) {
                toast.success('Invite declined');
                fetchInvites();
            } else {
                const error = await response.json();
                toast.error(error.detail || 'Failed to decline invite');
            }
        } catch (error) {
            toast.error('Error declining invite');
        } finally {
            setProcessing(false);
            setActionDialog({ open: false, invite: null, action: null });
            setDeclineReason('');
        }
    };

    const formatDate = (isoString) => {
        if (!isoString) return 'No deadline';
        return new Date(isoString).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        });
    };

    const getRequestTypeLabel = (type) => {
        const labels = {
            'jingle': 'Jingle',
            'full_song': 'Full Song',
            'ghostwrite': 'Ghostwrite',
            'custom': 'Custom'
        };
        return labels[type] || type || 'Not specified';
    };

    const getStatusBadge = (status) => {
        const styles = {
            sent: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
            accepted: 'bg-green-500/20 text-green-400 border-green-500/30',
            declined: 'bg-red-500/20 text-red-400 border-red-500/30',
            expired: 'bg-gray-500/20 text-gray-400 border-gray-500/30'
        };
        return styles[status] || styles.sent;
    };
    
    const handleViewProject = (invite) => {
        navigate(`/posted-projects/${invite.project_id}`);
    };

    const pendingInvites = invites.filter(inv => inv.status === 'sent');
    const acceptedInvites = invites.filter(inv => inv.status === 'accepted');
    const otherPastInvites = invites.filter(inv => inv.status !== 'sent' && inv.status !== 'accepted');

    return (
        <Layout>
            <div className="max-w-5xl mx-auto space-y-8">
                {/* Header */}
                <div>
                    <h1 className="text-3xl font-bold text-white mb-2">Project Invites</h1>
                    <p className="text-[#A3A3A3]">
                        Invitations from project owners to submit your music
                    </p>
                </div>

                {loading ? (
                    <div className="flex items-center justify-center py-20">
                        <Loader2 className="animate-spin text-[#F51E1E]" size={40} />
                    </div>
                ) : invites.length === 0 ? (
                    <div className="text-center py-20 bg-[#1A1A1A] border border-[#333] rounded-xl">
                        <Mail className="w-16 h-16 text-[#404040] mx-auto mb-4" />
                        <h2 className="text-xl font-semibold text-white mb-2">No Invites Yet</h2>
                        <p className="text-[#A3A3A3]">
                            When project owners invite you to submit, you'll see them here.
                        </p>
                    </div>
                ) : (
                    <div className="space-y-8">
                        {/* Pending Invites */}
                        {pendingInvites.length > 0 && (
                            <div>
                                <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                                    <Mail className="w-5 h-5 text-[#F51E1E]" />
                                    Pending Invites ({pendingInvites.length})
                                </h2>
                                <div className="space-y-4">
                                    {pendingInvites.map((invite, idx) => (
                                        <motion.div
                                            key={invite.id}
                                            initial={{ opacity: 0, y: 20 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: idx * 0.05 }}
                                        >
                                            <InviteCard 
                                                invite={invite}
                                                formatDate={formatDate}
                                                getRequestTypeLabel={getRequestTypeLabel}
                                                getStatusBadge={getStatusBadge}
                                                onAccept={() => setActionDialog({ open: true, invite, action: 'accept' })}
                                                onDecline={() => setActionDialog({ open: true, invite, action: 'decline' })}
                                                showActions
                                            />
                                        </motion.div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Accepted Invites - Can still view/submit if project is open */}
                        {acceptedInvites.length > 0 && (
                            <div>
                                <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                                    <CheckCircle className="w-5 h-5 text-green-400" />
                                    Accepted Projects ({acceptedInvites.length})
                                </h2>
                                <p className="text-sm text-[#A3A3A3] mb-4">
                                    Click on an open project to submit or view your submission
                                </p>
                                <div className="space-y-4">
                                    {acceptedInvites.map((invite, idx) => (
                                        <motion.div
                                            key={invite.id}
                                            initial={{ opacity: 0, y: 20 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: idx * 0.05 }}
                                        >
                                            <InviteCard 
                                                invite={invite}
                                                formatDate={formatDate}
                                                getRequestTypeLabel={getRequestTypeLabel}
                                                getStatusBadge={getStatusBadge}
                                                onViewProject={handleViewProject}
                                                showActions={false}
                                            />
                                        </motion.div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Other Past Invites (Declined/Expired) */}
                        {otherPastInvites.length > 0 && (
                            <div>
                                <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                                    <Clock className="w-5 h-5 text-[#666]" />
                                    Past Invites ({otherPastInvites.length})
                                </h2>
                                <div className="space-y-4">
                                    {otherPastInvites.map((invite, idx) => (
                                        <motion.div
                                            key={invite.id}
                                            initial={{ opacity: 0, y: 20 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: idx * 0.05 }}
                                        >
                                            <InviteCard 
                                                invite={invite}
                                                formatDate={formatDate}
                                                getRequestTypeLabel={getRequestTypeLabel}
                                                getStatusBadge={getStatusBadge}
                                                showActions={false}
                                            />
                                        </motion.div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Accept/Decline Dialog */}
                <AlertDialog 
                    open={actionDialog.open} 
                    onOpenChange={(open) => !open && setActionDialog({ open: false, invite: null, action: null })}
                >
                    <AlertDialogContent className="bg-[#1A1A1A] border-[#333]">
                        <AlertDialogHeader>
                            <AlertDialogTitle className="text-white">
                                {actionDialog.action === 'accept' ? 'Accept Invite' : 'Decline Invite'}
                            </AlertDialogTitle>
                            <AlertDialogDescription className="text-[#A3A3A3]">
                                {actionDialog.action === 'accept' ? (
                                    <>
                                        Accept this invite from <span className="text-white">{actionDialog.invite?.poster_name}</span> for 
                                        "<span className="text-white">{actionDialog.invite?.project_title}</span>"?
                                        <br /><br />
                                        You'll be redirected to the project to submit your track.
                                    </>
                                ) : (
                                    <>
                                        Decline this invite from <span className="text-white">{actionDialog.invite?.poster_name}</span>?
                                        <br /><br />
                                        <Textarea
                                            placeholder="Optional: Let them know why you're declining..."
                                            value={declineReason}
                                            onChange={(e) => setDeclineReason(e.target.value)}
                                            className="bg-[#262626] border-[#404040] text-white mt-2"
                                            rows={3}
                                        />
                                    </>
                                )}
                            </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                            <AlertDialogCancel className="bg-[#262626] border-[#404040] text-white hover:bg-[#333]">
                                Cancel
                            </AlertDialogCancel>
                            <AlertDialogAction
                                onClick={actionDialog.action === 'accept' ? handleAccept : handleDecline}
                                disabled={processing}
                                className={actionDialog.action === 'accept' 
                                    ? "bg-green-600 hover:bg-green-700 text-white"
                                    : "bg-red-600 hover:bg-red-700 text-white"
                                }
                            >
                                {processing ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                    actionDialog.action === 'accept' ? 'Accept' : 'Decline'
                                )}
                            </AlertDialogAction>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>
            </div>
        </Layout>
    );
}

function InviteCard({ invite, formatDate, getRequestTypeLabel, getStatusBadge, onAccept, onDecline, onViewProject, showActions }) {
    const canViewProject = invite.status === 'accepted' && invite.project_status === 'open';
    const projectClosed = invite.status === 'accepted' && invite.project_status === 'closed';
    
    return (
        <div 
            className={`bg-[#1A1A1A] border border-[#333] rounded-xl p-6 transition-colors ${
                canViewProject ? 'hover:border-[#F51E1E]/50 cursor-pointer' : 'hover:border-[#404040]'
            }`}
            onClick={() => canViewProject && onViewProject && onViewProject(invite)}
        >
            <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                    {/* Header */}
                    <div className="flex items-center gap-3 mb-3">
                        <Badge className={getStatusBadge(invite.status)}>
                            {invite.status}
                        </Badge>
                        {projectClosed && (
                            <Badge className="bg-gray-500/20 text-gray-400 border-gray-500/30">
                                <Lock className="w-3 h-3 mr-1" />
                                Project Closed
                            </Badge>
                        )}
                        <span className="text-xs text-[#666]">
                            Received {formatDate(invite.created_at)}
                        </span>
                    </div>

                    {/* Project Title */}
                    <h3 className="text-xl font-bold text-white mb-2">
                        {invite.project_title}
                    </h3>

                    {/* Poster Info */}
                    <div className="flex items-center gap-4 text-sm text-[#A3A3A3] mb-4">
                        <div className="flex items-center gap-1">
                            <User className="w-4 h-4" />
                            <span>{invite.poster_name}</span>
                        </div>
                        {invite.poster_company && (
                            <div className="flex items-center gap-1">
                                <Building2 className="w-4 h-4" />
                                <span>{invite.poster_company}</span>
                            </div>
                        )}
                    </div>

                    {/* Message */}
                    {invite.message && (
                        <div className="bg-[#262626] rounded-lg p-4 mb-4">
                            <div className="flex items-start gap-2">
                                <MessageSquare className="w-4 h-4 text-[#666] mt-0.5 flex-shrink-0" />
                                <p className="text-sm text-[#A3A3A3]">{invite.message}</p>
                            </div>
                        </div>
                    )}

                    {/* Meta Info */}
                    <div className="flex flex-wrap gap-4 text-sm">
                        {invite.request_type && (
                            <div className="flex items-center gap-1 text-[#A3A3A3]">
                                <Music className="w-4 h-4" />
                                <span>{getRequestTypeLabel(invite.request_type)}</span>
                            </div>
                        )}
                        {invite.deadline_at && (
                            <div className="flex items-center gap-1 text-[#A3A3A3]">
                                <Calendar className="w-4 h-4" />
                                <span>Due {formatDate(invite.deadline_at)}</span>
                            </div>
                        )}
                        {invite.budget_range && (
                            <div className="flex items-center gap-1 text-green-400">
                                <DollarSign className="w-4 h-4" />
                                <span>{invite.budget_range}</span>
                            </div>
                        )}
                    </div>

                    {/* Reference Links */}
                    {invite.reference_links?.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-3">
                            {invite.reference_links.map((link, idx) => (
                                <a
                                    key={idx}
                                    href={link.href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-1 text-xs text-[#F51E1E] hover:underline"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <ExternalLink className="w-3 h-3" />
                                    {link.text || 'Reference'}
                                </a>
                            ))}
                        </div>
                    )}
                </div>

                {/* Actions */}
                {showActions && (
                    <div className="flex flex-col gap-2">
                        <Button
                            onClick={(e) => { e.stopPropagation(); onAccept(); }}
                            className="bg-green-600 hover:bg-green-700 text-white"
                            data-testid={`accept-invite-${invite.id}`}
                        >
                            <CheckCircle className="w-4 h-4 mr-2" />
                            Accept
                        </Button>
                        <Button
                            onClick={(e) => { e.stopPropagation(); onDecline(); }}
                            variant="outline"
                            className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                            data-testid={`decline-invite-${invite.id}`}
                        >
                            <XCircle className="w-4 h-4 mr-2" />
                            Decline
                        </Button>
                    </div>
                )}
                
                {/* View Project Button for accepted invites with open projects */}
                {canViewProject && !showActions && (
                    <div className="flex items-center">
                        <Button
                            onClick={(e) => { e.stopPropagation(); onViewProject(invite); }}
                            className="bg-[#F51E1E] hover:bg-[#D91818] text-white"
                            data-testid={`view-project-${invite.id}`}
                        >
                            View Project
                            <ArrowRight className="w-4 h-4 ml-2" />
                        </Button>
                    </div>
                )}
            </div>
        </div>
    );
}
